import { z } from 'zod';
import { type Tasks } from '../../../workflow/schemaTypes';

export const COMMENT_MAX_LENGTH = 250;

export type Comment = {
  value?: string;
  metadataId?: string;
};

export type Photo = {
  id: string;
  previewUrl: string;
  metadataId?: string;
};

// The reason-tree leaf the shopper selected, in system terms. Present only on
// the tree-driven flow (PDP-1674/1675); resolution, claim create, and uploads
// key off { reasonId, domain, nodeId } from here rather than the legacy flat
// reasons list (which PDX no longer serves once the tree ships). Type is
// derived from the schema so the two can't drift — the desync between them is
// what stripped treeSelection on submit in the first place.
export const ReasonTreeSelectionSchema = z.object({
  // Non-blank, mirroring the flat reasonId guard below: getReasonInput's tree
  // branch sends this straight to care-api, which rejects a blank reason id.
  reasonId: z.string().refine((value) => value.trim().length > 0, {
    message: 'Selection required',
  }),
  domain: z.string().nullish(),
  nodeId: z.string().nullish(),
  code: z.string().nullish(),
  // Resolved label text, carried so submit sends a human-readable title.
  title: z.string().nullish(),
});

export type ReasonTreeSelectionData = z.infer<typeof ReasonTreeSelectionSchema>;

export type ReasonItemData = {
  reasonId?: string;
  nestedReasonId?: string;
  treeSelection?: ReasonTreeSelectionData;
  comments?: Comment;
  isCommentRequired?: string;
  // Per-reason minimum comment length from the tree leaf's settings; enforced
  // in the gate so a too-short comment fails here rather than at PDX.
  commentMinLength?: number;
  isNestedReasonRequired?: string;
  isPhotoRequired?: string;
  attestation?: { checked?: boolean; metadataId?: string };
  isAttestationRequired?: boolean;
  photos?: Photo[];
};

export type ReasonData = Record<Extract<Tasks, 'reason'>, ReasonItemData>;

export const ReasonsTaskSchema = z
  .object({
    // Reject blank reason ids, matching care-api's NotBlank contract (a blank
    // reasonId is rejected there with "reasonId must not be blank"). An empty
    // reasonId let the reason task read as complete before a reason was picked,
    // firing the delivery-options eligibility query with a blank reason; on
    // tree-only items (flat reasons list empty) the blank also drove
    // getReasonInput down its flat branch, which can never resolve an id and
    // always emitted "". refine (not .min/.trim) so whitespace-only ids are
    // caught too without transforming the value the zodResolver feeds back to
    // form state.
    reasonId: z
      .string({
        error: (issue) =>
          issue.input === undefined
            ? 'Selection required'
            : 'Selection required',
      })
      .refine((value) => value.trim().length > 0, {
        message: 'Selection required',
      }),
    nestedReasonId: z
      .string({
        error: (issue) =>
          issue.input === undefined
            ? 'Selection required'
            : 'Selection required',
      })
      .optional(),
    // Declared so it survives the zodResolver parse on Continue — a Zod object
    // strips undeclared keys, and without this the selected leaf is lost from
    // the persisted data, leaving tree-only reasons to submit a blank id.
    treeSelection: ReasonTreeSelectionSchema.optional(),
    isNestedReasonRequired: z.enum(['true', 'false']).optional(),
    comments: z
      .object({
        value: z
          .string({
            error: (issue) =>
              issue.input === undefined
                ? 'Comment required'
                : 'Comment required',
          })
          .max(COMMENT_MAX_LENGTH, {
            message: `Comment too long, maximum ${COMMENT_MAX_LENGTH} characters`,
          })
          .optional(),
        metadataId: z.string().optional(),
      })
      .optional(),
    isCommentRequired: z.enum(['true', 'false']).optional(),
    commentMinLength: z.number().optional(),
    isPhotoRequired: z.enum(['true', 'false']).optional(),
    attestation: z
      .object({
        checked: z.boolean().optional(),
        metadataId: z.string().optional(),
      })
      .optional(),
    isAttestationRequired: z.boolean().optional(),
    photos: z
      .array(
        z.object({
          id: z.string({
            error: (issue) =>
              issue.input === undefined ? 'Photo required' : 'Photo Required',
          }),
          metadataId: z.string().optional(),
          previewUrl: z.string().optional(),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    const isNestedReasonRequired = data.isNestedReasonRequired === 'true';
    if (data.reasonId) {
      if (isNestedReasonRequired && !data.nestedReasonId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Missing nested reason',
        });
      }
    }

    const shouldValidateFields = Boolean(
      isNestedReasonRequired
        ? data.reasonId && data.nestedReasonId
        : data.reasonId,
    );

    if (
      shouldValidateFields &&
      data.isCommentRequired === 'true' &&
      (!data.comments?.value || data.comments.value.length < 1)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Missing comments',
      });
    }

    // Per-reason minimum comment length (tree leaf settings). Only when a
    // comment was actually entered — emptiness is covered by the required
    // check above.
    if (
      shouldValidateFields &&
      data.commentMinLength != null &&
      data.comments?.value &&
      data.comments.value.length < data.commentMinLength
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Comment too short, minimum ${data.commentMinLength} characters`,
      });
    }

    if (
      shouldValidateFields &&
      data.isPhotoRequired === 'true' &&
      (!data.photos || data.photos.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Missing photo',
      });
    }

    if (
      shouldValidateFields &&
      data.isAttestationRequired === true &&
      data.attestation?.checked !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Missing attestation',
      });
    }
  });

export type ReasonsTaskSchemaType = z.infer<typeof ReasonsTaskSchema>;
